import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import styled, { keyframes } from "styled-components";
import {
  colorBackground,
  colorFieldsBorder,
  colorLightGray,
  colorText,
  colorTextAdditional,
  colorTextNew,
} from "../../../utils/palette";
import { borderRadius } from "../../../utils/styles";
import { LinkSpan } from "../../../shared/components/form/FormElements";
import { post, postApi, putApi } from "../../../api/getUser";
import { useToast } from "../../../contexts/ToastContext";
import { CustomLoader } from "../../../shared/components/TableElements";
import getUnixTime from "date-fns/getUnixTime";
import { getListingDisplayDate } from "../../../shared/helpers";

function formatPausedDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTimeAgo(value) {
  if (!value) return undefined;
  // Handle unix timestamps in seconds (< 1e10) vs milliseconds
  const ms =
    typeof value === "number" && value < 1e10
      ? value * 1000
      : new Date(value).getTime();
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

// Returns calendar-day difference between a UTC datetime string and today (local tz)
function getLastRunDayCount(value) {
  if (!value) return null;
  let ms;
  if (typeof value === "string") {
    // Normalise "2026-09-11 06:19:45" → "2026-09-11T06:19:45Z"
    // but leave already-valid ISO strings (containing T or ending with Z) untouched
    const normalised =
      value.trim().includes("T") || value.trim().endsWith("Z")
        ? value.trim()
        : value.trim().replace(" ", "T") + "Z";
    ms = new Date(normalised).getTime();
  } else if (typeof value === "number") {
    ms = value < 1e10 ? value * 1000 : value;
  } else {
    ms = new Date(value).getTime();
  }
  if (isNaN(ms)) return null;
  const now = new Date();
  const todayStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const runDate = new Date(ms);
  const runStart = new Date(
    runDate.getFullYear(),
    runDate.getMonth(),
    runDate.getDate(),
  ).getTime();
  return Math.round((todayStart - runStart) / 86400000);
}

function mapInsight(row) {
  return {
    id: row.insight_id,
    severity: row.severity,
    title: row.title,
    description: row.narrative,
    entityName: row.entity_name,
    updatedAt: formatTimeAgo(row.last_seen_at),
    module: row.module,
    checkId: row.check_id,
    supportingData: row.supporting_data,
  };
}

// ── Severity config ────────────────────────────────────────────────────────────

const SEVERITY = {
  critical: {
    labelKey: "severity_critical",
    label: "Critical",
    color: "#D32F2F",
    bg: "#FFF5F5",
    bgColor: "#FFEBEE",
    darkBg: "rgba(211,47,47,0.12)",
    icon: "!",
  },
  warning: {
    labelKey: "warning",
    label: "Warning",
    color: "#E65100",
    bg: "#FFF8F0",
    bgColor: "#FFF8E1",
    darkBg: "rgba(230,81,0,0.12)",
    icon: "▲",
  },
  info: {
    labelKey: "severity_info",
    label: "Info",
    color: "#1565C0",
    bg: "#F0F6FF",
    bgColor: "#BBDEFB",
    darkBg: "rgba(21,101,192,0.12)",
    icon: "i",
  },
};

const SEVERITY_ORDER = ["critical", "warning", "info"];

// ── Panel shell ───────────────────────────────────────────────────────────────

const Panel = styled.div`
  position: fixed;
  top: 62px;
  right: 0;
  bottom: 0;
  width: 380px;
  background: ${colorBackground};
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
  border-left: 1px solid ${colorFieldsBorder};
  z-index: 900;
  animation: slideIn 0.22s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @media (max-width: 480px) {
    width: 100%;
    top: 0;
  }
`;

// ── Header ────────────────────────────────────────────────────────────────────

const PanelHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${colorFieldsBorder};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;

const PanelHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PanelTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${colorTextNew};
  margin: 0;
`;

const TotalBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: ${colorLightGray};
  //color: #fff;
  font-size: 11px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: none;
  color: ${colorTextAdditional};
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition: background 0.15s;
  font-family: inherit;

  &:hover {
    background: ${colorFieldsBorder};
    color: ${colorText};
  }
`;

const SettingsButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${colorFieldsBorder};
  background: none;
  color: ${colorTextAdditional};
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  transition:
    background 0.15s,
    color 0.15s;
  font-family: inherit;

  &:hover {
    background: ${colorFieldsBorder};
    color: ${colorText};
  }
`;

const HeaderSubtext = styled.div`
  font-size: 11px;
  color: ${colorTextAdditional};
  margin-top: 4px;
`;

// ── Body ──────────────────────────────────────────────────────────────────────

const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0 16px;
`;

const SeveritySection = styled.div`
  margin-bottom: 4px;
`;

const SeverityHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px 8px;
`;

const SeverityLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  //color: ${({ $color }) => $color};
`;

const SeverityCount = styled.span`
  font-size: 11px;
  font-weight: 600;
  //color: ${({ $color }) => $color};
  //background: ${({ $bg }) => $bg};
  background: ${colorLightGray};
  padding: 1px 7px;
  border-radius: 10px;
`;

// ── Insight card ──────────────────────────────────────────────────────────────

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  margin: 0 16px 8px;
  background: ${({ $bgColor }) => $bgColor};
  border-radius: ${borderRadius};
`;

const CardDismiss = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colorTextAdditional};
  font-size: 14px;
  line-height: 1;
  border-radius: 4px;
  padding: 0;
  font-family: inherit;
  transition:
    background 0.15s,
    color 0.15s;

  &:hover {
    background: ${colorFieldsBorder};
    color: ${colorText};
  }
`;

const ModuleLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  //display: block;
  //color: ${({ $color }) => $color};
`;

const IconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  flex-shrink: 0;
  margin-top: 1px;
`;

const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #2b3240;
  margin: 0 0 6px;
  line-height: 1.35;
  letter-spacing: 0.2px;
`;

const CardDesc = styled.div`
  font-size: 12px;
  color: #2b3240;
  margin: 0 0 6px;
  line-height: 1.6;
  overflow: hidden;
  letter-spacing: 0.2px;

  ${({ $expanded }) => !$expanded && `max-height: 3.4em;`}

  p {
    margin: 0 0 6px;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 4px 0;
    padding-left: 16px;
  }
  li {
    margin-bottom: 2px;
  }
  strong {
    color: #2b3240;
    font-weight: 600;
  }
`;

const ReadMoreToggle = styled.button`
  font-size: 12px;
  font-weight: 600;
  color: ${colorTextAdditional};
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ViewLink = styled.a`
  font-size: 12px;
  font-weight: 500;
  color: #1565c0;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const UpdatedAt = styled.span`
  font-size: 11px;
  color: ${colorTextAdditional};
`;

const ViewDataButton = styled.button`
  font-size: 12px;
  font-weight: 500;
  color: #1565c0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

// ── Supporting data modal ─────────────────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const ModalBox = styled.div`
  background: ${colorBackground};
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  max-width: 900px;
  width: 90vw;
  max-height: 80vh;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid ${colorFieldsBorder};
  flex-shrink: 0;
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1b2a4a;
  margin: 0;
  line-height: 1.35;
`;

const ModalSubtitle = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
  margin-top: 4px;
`;

const ModalClose = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: none;
  color: ${colorTextAdditional};
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: ${colorFieldsBorder};
    color: ${colorText};
  }
`;

const ModalBody = styled.div`
  overflow: auto;
  flex: 1;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const TH = styled.th`
  text-align: ${({ $right }) => ($right ? "right" : "left")};
  padding: 10px 16px;
  background: ${colorBackground};
  color: ${colorTextAdditional};
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  border-bottom: 1px solid ${colorFieldsBorder};
  position: sticky;
  top: 0;
`;

const TD = styled.td`
  padding: 10px 16px;
  color: ${colorText};
  border-bottom: 1px solid ${colorFieldsBorder};
  white-space: nowrap;
  text-align: ${({ $right }) => ($right ? "right" : "left")};
  font-weight: ${({ $bold }) => ($bold ? "700" : "400")};

  tr:last-child & {
    border-bottom: none;
  }
`;

const LVGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 16px 24px;
  padding: 20px 20px;
  align-items: baseline;
`;

const LVLabel = styled.span`
  font-size: 13px;
  color: ${colorTextAdditional};
  white-space: nowrap;
`;

const LVValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${colorText};
  white-space: nowrap;
`;

const CURRENCY_KEY_RE = /cost|amount|price|revenue|spend|expense|fee|charge/i;
const MULTIPLIER_KEY_RE = /multiplier|ratio|factor/i;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;
const NUMERIC_RE = /^-?\d+(\.\d+)?$/;

function sentenceCase(key) {
  const s = key.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

function formatDataDate(val) {
  if (!val || !ISO_DATE_RE.test(String(val))) return null;
  const normalised = String(val).replace(" ", "T") + (String(val).includes("T") ? "" : "Z");
  const d = new Date(normalised);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatValue(key, val, currencySymbol) {
  if (val === null || val === undefined || val === "") return "—";
  const str = String(val);

  const dated = formatDataDate(str);
  if (dated) return dated;

  if (MULTIPLIER_KEY_RE.test(key)) return `${str}×`;

  if (CURRENCY_KEY_RE.test(key) && NUMERIC_RE.test(str)) {
    return `${currencySymbol}${Number(val).toFixed(2)}`;
  }

  return str || "—";
}

function isNumericValue(val) {
  if (val === null || val === undefined) return false;
  return NUMERIC_RE.test(String(val));
}

function SupportingDataModal({ insight, onClose }) {
  const { t } = useTranslation("common");
  const currencySymbol = useSelector((state) => state.units.currency ?? "");
  const rows = insight.supportingData ?? [];
  const columns = rows.length ? Object.keys(rows[0]) : [];
  const isSingleRow = rows.length === 1;

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <div>
            <ModalTitle>{insight.title}</ModalTitle>
            {/* {rows.length > 0 && (
              <ModalSubtitle>
                {t("occurrences_count", { count: rows.length })}
              </ModalSubtitle>
            )} */}
          </div>
          <ModalClose onClick={onClose}>✕</ModalClose>
        </ModalHeader>
        <ModalBody>
          {rows.length === 0 ? (
            <EmptyState>{t("no_data_available")}</EmptyState>
          ) : isSingleRow ? (
            <LVGrid>
              {columns.map((col) => {
                const val = rows[0][col];
                return (
                  <React.Fragment key={col}>
                    <LVLabel>{sentenceCase(col)}</LVLabel>
                    <LVValue>{formatValue(col, val, currencySymbol)}</LVValue>
                  </React.Fragment>
                );
              })}
            </LVGrid>
          ) : (
            <StyledTable>
              <thead>
                <tr>
                  {columns.map((col) => {
                    const firstVal = rows[0][col];
                    const rightAlign = isNumericValue(firstVal) && !formatDataDate(String(firstVal ?? ""));
                    return (
                      <TH key={col} $right={rightAlign}>
                        {sentenceCase(col)}
                      </TH>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i}>
                    {columns.map((col) => {
                      const val = row[col];
                      const rightAlign = isNumericValue(val) && !formatDataDate(String(val ?? ""));
                      return (
                        <TD key={col} $right={rightAlign} $bold={rightAlign}>
                          {formatValue(col, val, currencySymbol)}
                        </TD>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          )}
        </ModalBody>
      </ModalBox>
    </ModalBackdrop>
  );
}

// ── Settings modal ────────────────────────────────────────────────────────────

const SettingsModalBox = styled(ModalBox)`
  max-width: 480px;
`;

const SettingsModalHeader = styled.div`
  padding: 20px 24px 16px;
  border-bottom: 1px solid ${colorFieldsBorder};
  flex-shrink: 0;
`;

const SettingsModalTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`;

const SettingsModalTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SettingsModalTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colorText};
  margin: 0;
`;

const SettingsModalSubtext = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
`;

const SettingsModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  overflow-y: auto;
`;

const SettingRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const SettingInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SettingLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colorText};
  margin-bottom: 3px;
`;

const SettingDesc = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
  line-height: 1.5;
`;

const Toggle = styled.label`
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  flex-shrink: 0;
  cursor: pointer;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  span {
    position: absolute;
    inset: 0;
    background: #c5cbd3;
    border-radius: 12px;
    transition: background 0.2s;

    &::before {
      content: "";
      position: absolute;
      width: 18px;
      height: 18px;
      border-radius: 50%;
      background: #fff;
      top: 3px;
      left: 3px;
      transition: transform 0.2s;
    }
  }

  input:checked + span {
    background: #6254c7;

    &::before {
      transform: translateX(18px);
    }
  }
`;

const FrequencySection = styled.div``;

const FrequencySectionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colorText};
  margin-bottom: 4px;
`;

const FrequencySectionDesc = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
  margin-bottom: 12px;
`;

const FrequencyButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const FreqBtn = styled.button`
  flex: 1;
  padding: 9px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ $active, $disabled }) =>
      $disabled && $active
        ? "#B0A8E0"
        : $active
          ? "#6254C7"
          : colorFieldsBorder};
  background: ${({ $active, $disabled }) =>
    $disabled && $active ? "#EAE8F5" : $active ? "#6254C7" : "transparent"};
  color: ${({ $active, $disabled }) =>
    $disabled && $active
      ? "#9B94D0"
      : $disabled
        ? "#C5CBD3"
        : $active
          ? "#fff"
          : colorText};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  font-family: inherit;
  transition: all 0.15s;
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};

  &:hover {
    ${({ $active, $disabled }) =>
      !$active && !$disabled && `border-color: #6254C7; color: #6254C7;`}
  }
`;

const PausedBanner = styled.div`
  background: #f8f8fa;
  border: 1px solid ${colorFieldsBorder};
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  color: ${colorText};
  line-height: 1.5;
`;

const ResumingNote = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
  margin-top: 8px;
`;

const CreditInfoBox = styled.div`
  background: ${colorLightGray};
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  color: ${colorText};
  line-height: 1.6;
`;

const SettingsModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px 20px;
  border-top: 1px solid ${colorFieldsBorder};
  gap: 12px;
`;

const TopUpLink = styled.button`
  background: none;
  border: none;
  color: #6254c7;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 8px;
`;

const CancelBtn = styled.button`
  padding: 8px 18px;
  border-radius: 6px;
  border: 1px solid ${colorFieldsBorder};
  background: transparent;
  color: ${colorText};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: ${colorLightGray};
  }
`;

const SaveBtn = styled.button`
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  background: #1b2a4a;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: #253d6e;
  }
`;

export function InsightsSettingsModal({
  onClose,
  isEnabled,
  frequency,
  onSave,
  pausedBy,
  pausedAt,
  hideTopUpLink = false,
}) {
  const { t } = useTranslation("common");
  const history = useHistory();
  const { userName } = useSelector((state) => state.user);
  const [localEnabled, setLocalEnabled] = useState(isEnabled);
  const [localFrequency, setLocalFrequency] = useState(frequency);
  const [isLoading, setIsLoading] = useState(false);

  const FREQ_CREDITS = {
    0: { label: t("daily"), runs: 30, total: 150 },
    1: { label: t("weekly"), runs: 4, total: 20 },
    2: { label: t("monthly"), runs: 1, total: 5 },
  };
  const freq = useMemo(
    () => FREQ_CREDITS[localFrequency ?? 1],
    [localFrequency],
  );

  async function handleSave() {
    await onSave(localEnabled, localFrequency, setIsLoading);
    onClose();
  }

  return (
    <ModalBackdrop onClick={onClose}>
      <SettingsModalBox onClick={(e) => e.stopPropagation()}>
        <SettingsModalHeader>
          <SettingsModalTitleRow>
            <SettingsModalTitleLeft>
              <i
                className="fi fi-rr-sparkles"
                style={{
                  fontSize: 14,
                  lineHeight: 1,
                  position: "relative",
                  top: 1,
                  color: "#6254C7",
                }}
              />
              <SettingsModalTitle>
                {t("ai_insights_settings")}
              </SettingsModalTitle>
            </SettingsModalTitleLeft>
            <ModalClose onClick={onClose}>✕</ModalClose>
          </SettingsModalTitleRow>
          <SettingsModalSubtext>
            {t("ai_insights_settings_desc")}
          </SettingsModalSubtext>
        </SettingsModalHeader>

        <SettingsModalBody>
          {!localEnabled && (
            <PausedBanner>
              <Trans
                t={t}
                i18nKey="paused_by_text"
                values={{
                  pausedBy: pausedBy || userName,
                  pausedAt: getListingDisplayDate(
                    pausedAt || Math.round(Date.now() / 1000),
                  ),
                }}
                components={{ strong: <strong /> }}
              />
            </PausedBanner>
          )}

          <SettingRow>
            <SettingInfo>
              <SettingLabel>{t("generate_insights")}</SettingLabel>
              <SettingDesc>{t("generate_insights_desc")}</SettingDesc>
            </SettingInfo>
            <Toggle>
              <input
                type="checkbox"
                checked={localEnabled}
                onChange={(e) => setLocalEnabled(e.target.checked)}
              />
              <span />
            </Toggle>
          </SettingRow>

          <FrequencySection>
            <FrequencySectionLabel>{t("frequency")}</FrequencySectionLabel>
            <FrequencySectionDesc>{t("frequency_desc")}</FrequencySectionDesc>
            <FrequencyButtons>
              {Object.entries(FREQ_CREDITS).map(([key, cfg]) => (
                <FreqBtn
                  key={key}
                  $active={localFrequency === Number(key)}
                  $disabled={!localEnabled}
                  onClick={() => setLocalFrequency(Number(key))}
                >
                  {t(`freq_${key}`)}
                </FreqBtn>
              ))}
            </FrequencyButtons>
          </FrequencySection>

          <div>
            <CreditInfoBox>
              {t("credit_info_prefix")} <strong>5 {t("credits")}</strong>
              {" · "}
              {freq.label} ≈ {freq.runs} {t("runs_per_month")} ≈{" "}
              <strong>
                {freq.total} {t("credits_per_month")}
              </strong>
            </CreditInfoBox>
            {!localEnabled && <ResumingNote>{t("resuming_note")}</ResumingNote>}
          </div>
        </SettingsModalBody>

        <SettingsModalFooter>
          {!hideTopUpLink && (
            <TopUpLink
              onClick={() => {
                onClose();
                history.push("/billing");
              }}
            >
              {t("top_up_credits")} →
            </TopUpLink>
          )}
          <FooterActions>
            <CancelBtn onClick={onClose}>{t("cancel")}</CancelBtn>
            <SaveBtn onClick={handleSave}>
              {isLoading ? <CustomLoader /> : t("save")}
            </SaveBtn>
          </FooterActions>
        </SettingsModalFooter>
      </SettingsModalBox>
    </ModalBackdrop>
  );
}

// ── Paused banner (inline, above insights list) ───────────────────────────────

const PausedBannerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 16px 8px;
  padding: 12px 16px;
  background: #f0f1f5;
  border-radius: 8px;
`;

const PausedBannerLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const PausedBannerText = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${colorText};
`;

const PausedBannerDesc = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
`;

// ── Paused panel state (full-page, kept for reference) ────────────────────────

const PausedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 28px;
  text-align: center;
  gap: 0;
`;

const PauseIconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f0f1f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #9198a8;
  font-size: 20px;
`;

const PausedTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${colorText};
  margin-bottom: 8px;
`;

const PausedDesc = styled.div`
  font-size: 13px;
  color: ${colorTextAdditional};
  line-height: 1.6;
  margin-bottom: 4px;
`;

const PausedBy = styled.div`
  font-size: 12px;
  color: ${colorTextAdditional};
  margin-bottom: 20px;
`;

const ResumeButton = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  border: none;
  background: #6254c7;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s;

  &:hover {
    background: #5245b0;
  }
`;

// ── Empty state ───────────────────────────────────────────────────────────────

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  gap: 8px;
  color: ${colorTextAdditional};
  font-size: 13px;
`;

const EmptyStateTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1a1a2e;
  text-align: center;
`;

const EmptyStateDesc = styled.div`
  font-size: 13px;
  color: ${colorTextAdditional};
  text-align: center;
  line-height: 1.5;
`;

// ── Loading skeleton ──────────────────────────────────────────────────────────

const shimmer = keyframes`
  0%   { background-position: 200% center; }
  100% { background-position: -200% center; }
`;

const SkeletonCard = styled.div`
  margin: 0 16px 8px;
  padding: 12px 16px;
  border-radius: ${borderRadius};
  border-left: 3px solid ${colorFieldsBorder};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonLine = styled.div`
  height: ${({ $h }) => $h || "12px"};
  width: ${({ $w }) => $w || "100%"};
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    ${colorFieldsBorder} 25%,
    ${colorBackground} 50%,
    ${colorFieldsBorder} 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

function LoadingSkeleton() {
  return (
    <>
      {[80, 65, 90].map((w, i) => (
        <SkeletonCard key={i}>
          <SkeletonLine $h="13px" $w={`${w}%`} />
          <SkeletonLine $h="11px" $w="95%" />
          <SkeletonLine $h="11px" $w="60%" />
        </SkeletonCard>
      ))}
    </>
  );
}

// ── Inline markdown renderer ──────────────────────────────────────────────────
// Handles: **bold**, - bullet lists, blank-line paragraph breaks

function InlineText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={i}>{part.slice(2, -2)}</strong>
        ) : (
          part
        ),
      )}
    </>
  );
}

function MarkdownText({ children }) {
  if (!children) return null;
  const blocks = children.split(/\n\n+/);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        const isList = lines.every((l) => /^-\s/.test(l));
        if (isList) {
          return (
            <ul key={i}>
              {lines.map((l, j) => (
                <li key={j} style={{ fontWeight: 400, color: "#000000" }}>
                  <InlineText text={l.replace(/^-\s/, "")} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} style={{ color: "#000000" }}>
            <InlineText text={block} />
          </p>
        );
      })}
    </>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function InsightCard({ insight, onDismiss, onViewData, onNavigate }) {
  const { t } = useTranslation("common");
  const sev = SEVERITY[insight.severity] ?? SEVERITY.info;
  const moduleName = t(insight.module) ?? insight.module;
  const [expanded, setExpanded] = useState(false);
  return (
    <Card $bgColor={sev.bgColor}>
      {/* <CardDismiss onClick={onDismiss} aria-label="Dismiss">
        {" "}
        <i className="fi fi-rr-cross-small" />
      </CardDismiss> */}
      <ModuleLabel>{moduleName}</ModuleLabel>
      <CardContent>
        <CardTitle>{insight.title}</CardTitle>
        {insight.description && (
          <CardDesc $expanded={expanded}>
            <MarkdownText>{insight.description}</MarkdownText>
          </CardDesc>
        )}
        <CardFooter>
          {insight.description && (
            <ReadMoreToggle onClick={() => setExpanded((v) => !v)}>
              {expanded ? t("read_less") : t("read_more")}
            </ReadMoreToggle>
          )}
          {insight.module === "reminders" && insight.checkId === "RM-02" ? (
            <ViewDataButton
              onClick={() => onNavigate("/reminders/maintenancereminders")}
            >
              {t("view_data")} →
            </ViewDataButton>
          ) : insight.supportingData?.length > 0 ? (
            <ViewDataButton onClick={onViewData}>
              {t("view_data")} →
            </ViewDataButton>
          ) : (
            <span />
          )}
        </CardFooter>
      </CardContent>
    </Card>
  );
}

export default function InsightsPanel({ isOpen, onClose, onCountChange }) {
  const { t } = useTranslation("common");
  const history = useHistory();
  const { showToast } = useToast();
  const users = useSelector((state) => state.users.list);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(new Set());
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { userId, userName } = useSelector((state) => state.user);
  const [isEnabled, setIsEnabled] = useState(false);
  const [frequency, setFrequency] = useState(1);
  const [pausedBy, setPausedBy] = useState("");
  const [pausedAt, setPausedAt] = useState(null);
  const [lastRunAt, setLastRunAt] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const hasFetched = React.useRef(false);

  useEffect(() => {
    if (isOpen) {
      window.fcWidget?.hide();
    } else {
      window.fcWidget?.show();
      setSelectedInsight(null);
    }
  }, [isOpen]);

  async function fetchInsights() {
    setLoading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await post("dashboardV2", { route: "ai_insights", tz });
      const rows = res?.data?.rows ?? [];
      const mapped = rows.map(mapInsight);
      setInsights(mapped);
      onCountChange?.(mapped.length);
    } catch {
      // keep empty
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isOpen || hasFetched.current) return;

    hasFetched.current = true;

    async function load() {
      // Fetch settings first; only load insights if active
      let active = true;
      try {
        const res = await putApi(
          { querystring: "get_ai_insight_settings" },
          "dashboard",
        );
        if (res?.success && res?.data?.length > 0) {
          const { frequency, is_active, paused_by, paused_at, last_run_at } =
            res.data[0];
          active = is_active === 1;
          setIsEnabled(active);
          setFrequency(frequency);
          setLastRunAt(last_run_at || null);
          if (!active) {
            const pausedUser = users.find((u) => u.id === paused_by);
            setPausedBy(pausedUser?.name ?? paused_by ?? "");
            setPausedAt(paused_at || null);
          }
        }
      } catch (err) {
        console.error("Error fetching insights settings:", err);
      } finally {
        setSettingsLoading(false);
      }

      await fetchInsights();
    }
    load();
  }, [isOpen]);

  if (!isOpen) return null;

  const FREQ_LABELS = {
    0: t("runs_daily"),
    1: t("runs_weekly"),
    2: t("runs_monthly"),
  };
  const lastRunDays = getLastRunDayCount(lastRunAt);
  const lastRunText =
    lastRunDays === null
      ? t("not_run_yet")
      : lastRunDays === 0
        ? t("last_run_today")
        : t("last_run_days_ago", { count: lastRunDays });
  const scheduleHint = isEnabled
    ? [
        lastRunText,
        FREQ_LABELS[frequency] ?? FREQ_LABELS[1],
        t("5_credits_per_run"),
      ]
        .filter(Boolean)
        .join(" · ")
    : [lastRunText, t("paused")].filter(Boolean).join(" · ");

  const visible = insights.filter((i) => !dismissed.has(i.id));

  const grouped = SEVERITY_ORDER.reduce((acc, sev) => {
    const items = visible.filter((i) => i.severity === sev);
    if (items.length) acc.push({ sev, items });
    return acc;
  }, []);

  const saveInsightsSettings = async (newEnabled, newFrequency, setLoading) => {
    const nowSec = Math.floor(Date.now() / 1000);
    const apiData = {
      is_active: newEnabled ? 1 : 0,
      frequency: Number(newFrequency),
      querystring: "save_ai_insight_settings",
      paused_by: newEnabled ? "" : userId,
      paused_at: newEnabled ? null : nowSec,
    };
    try {
      setLoading(true);
      const res = await putApi(apiData, "dashboard");
      if (res.success) {
        setIsEnabled(newEnabled);
        setFrequency(newFrequency);
        if (!newEnabled) {
          setPausedBy(userName ?? "");
          setPausedAt(nowSec);
        } else {
          setPausedBy("");
          setPausedAt(null);
          fetchInsights();
        }
        showToast("success", t("insights_settings_saved"));
      }
    } catch (err) {
      console.error("Error saving insights settings:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Panel>
        <PanelHeader>
          <PanelHeaderTop>
            <HeaderLeft>
              <div className="d-flex gap-2 align-items-center">
                <i
                  className="fi fi-rr-sparkles"
                  style={{
                    fontSize: 14,
                    lineHeight: 1,
                    position: "relative",
                    top: 1,
                  }}
                />
                <PanelTitle>{t("ai_insights")}</PanelTitle>
              </div>
              {visible.length > 0 && <TotalBadge>{visible.length}</TotalBadge>}
            </HeaderLeft>
            <div className="d-flex gap-2 align-items-center">
              <SettingsButton
                title={t("settings")}
                onClick={() => setSettingsOpen(true)}
              >
                <i
                  className="fi fi-rr-settings"
                  style={{ fontSize: 13, lineHeight: 1 }}
                />
              </SettingsButton>
              <CloseButton onClick={onClose}>✕</CloseButton>
            </div>
          </PanelHeaderTop>
          <HeaderSubtext>{scheduleHint}</HeaderSubtext>
        </PanelHeader>

        <PanelBody>
          {settingsLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {!isEnabled && (
                <PausedBannerRow>
                  <PausedBannerLeft>
                    <PausedBannerText>
                      {t("insights_paused_title")}
                    </PausedBannerText>
                    <PausedBannerDesc>
                      {t("insights_paused_desc")}
                    </PausedBannerDesc>
                  </PausedBannerLeft>
                  <ResumeButton
                    onClick={() =>
                      saveInsightsSettings(
                        true,
                        frequency,
                        setIsLoadingSettings,
                      )
                    }
                  >
                    {isLoadingSettings ? <CustomLoader /> : t("resume")}
                  </ResumeButton>
                </PausedBannerRow>
              )}
              {loading ? (
                <LoadingSkeleton />
              ) : grouped.length === 0 ? (
                <EmptyState>
                  <EmptyStateTitle>
                    {lastRunAt
                      ? t("insights_nothing_title")
                      : t("insights_not_run_title")}
                  </EmptyStateTitle>
                  <EmptyStateDesc>
                    {lastRunAt
                      ? t("insights_nothing_desc")
                      : t("insights_not_run_desc")}
                  </EmptyStateDesc>
                </EmptyState>
              ) : (
                grouped.map(({ sev, items }) => {
                  const cfg = SEVERITY[sev];
                  return (
                    <SeveritySection key={sev}>
                      <SeverityHeader>
                        <SeverityLabel $color={cfg.color}>
                          {t(cfg.labelKey, { defaultValue: cfg.label })}
                        </SeverityLabel>
                        <SeverityCount $color={cfg.color} $bg={cfg.bg}>
                          {items.length}
                        </SeverityCount>
                      </SeverityHeader>
                      {items.map((insight) => (
                        <InsightCard
                          key={insight.id}
                          insight={insight}
                          onDismiss={() =>
                            setDismissed(
                              (prev) => new Set([...prev, insight.id]),
                            )
                          }
                          onViewData={() => setSelectedInsight(insight)}
                          onNavigate={(path) => {
                            onClose();
                            history.push(path);
                          }}
                        />
                      ))}
                    </SeveritySection>
                  );
                })
              )}
            </>
          )}
        </PanelBody>
      </Panel>

      {selectedInsight && (
        <SupportingDataModal
          insight={selectedInsight}
          onClose={() => setSelectedInsight(null)}
        />
      )}

      {settingsOpen && (
        <InsightsSettingsModal
          onClose={() => setSettingsOpen(false)}
          isEnabled={isEnabled}
          frequency={frequency}
          onSave={saveInsightsSettings}
          pausedBy={pausedBy}
          pausedAt={pausedAt}
        />
      )}
    </>
  );
}
